import React, { useEffect, useState } from 'react';
import useUserStore from '../../../services/useUserStore';
import { useAuthStore } from '../../../services/useAuthStore';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import DeleteUserDialog from './DeleteUserDialog';
import './UserManagement.css';
import FullScreenLoader from '../../FullScreenLoader/FullScreenLoader';

const getRoleBadgeClass = (role) => {
  const classes = {
    ADMIN: 'badge-danger',
    MANAGER: 'badge-primary',
    OPERATOR: 'badge-success',
    FINANCE: 'badge-warning'
  };
  return classes[role] || 'badge-default';
};

const UserManagement = () => {
  const { users, loading, error, fetchUsers } = useUserStore();
  const { membership } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Access control: Only ADMIN and MANAGER (role levels 1 and 2)
  const userRole = membership?.role;
  const hasAccess = userRole === 'admin' || userRole === 'manager';

  if (!hasAccess) {
    return (
      <div className="co-section">
        <h2>Gerenciamento de Usuários</h2>
        <p className="co-error">
          Acesso negado: Você não tem permissão para gerenciar usuários.
        </p>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="co-section">
        < FullScreenLoader message="Carregando Gerenciamento Usuários..." />
      </div>
    );
  }

  return (
    <div className="co-section">
      <div className="user-management-header">
        <div>
          <h2>Gerenciamento de Usuários</h2>
          <p className="co-section-subtitle">
            Visualize, crie, edite e remova usuários do workspace.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Adicionar Usuário
        </button>
      </div>

      {error && <p className="co-error">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}

      <div className="co-group">
        <div className="user-table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Perfil</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={user.is_active ? 'status-active' : 'status-inactive'}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => setEditingUser(user)}
                        title="Editar usuário"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => setDeletingUser(user)}
                        title="Excluir usuário"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <p className="empty-state">Nenhum usuário encontrado.</p>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            fetchUsers();
          }}
        />
      )}

      {deletingUser && (
        <DeleteUserDialog
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onSuccess={() => {
            setDeletingUser(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
